import { ID, Task, UserSettings } from '@griply/types';

import { getDuplicatedCustomTaskOrder } from './get-duplicated-custom-task-order';
import { insertDuplicatesAfterSelection } from './insert-duplicates-after-selection';

type DuplicatedTaskOrderSettingsInput = Pick<
  UserSettings,
  'customTaskOrder' | 'todoSorting' | 'todayGroupSorting'
>;

type TaskPairs = Array<{ oldTask: Task; newTask: Task }>;

// Runs the same below-selection insert over a flat per-bucket order map, keeping
// only the buckets that actually changed so the persisted write stays minimal
// and Firestore's dot-path merge leaves untouched buckets intact.
const getUpdatedOrderRecord = (
  record: Record<string, ID[]> | undefined,
  taskPairs: TaskPairs,
): Record<string, ID[]> | undefined => {
  if (!record) {
    return undefined;
  }

  const result: Record<string, ID[]> = {};
  let changed = false;

  Object.entries(record).forEach(([key, ids]) => {
    if (!Array.isArray(ids)) {
      return;
    }
    const updated = insertDuplicatesAfterSelection(ids, taskPairs);
    if (updated !== ids) {
      result[key] = updated;
      changed = true;
    }
  });

  return changed ? result : undefined;
};

const getUpdatedTodayGroupSorting = (
  todayGroupSorting: UserSettings['todayGroupSorting'],
  taskPairs: TaskPairs,
): UserSettings['todayGroupSorting'] | undefined => {
  if (!todayGroupSorting) {
    return undefined;
  }

  const result: Record<string, { sorting: Record<string, ID[]> }> = {};
  let changed = false;

  Object.entries(todayGroupSorting).forEach(([mode, value]) => {
    const updatedSorting = getUpdatedOrderRecord(
      value?.sorting as Record<string, ID[]> | undefined,
      taskPairs,
    );
    if (updatedSorting) {
      result[mode] = { sorting: updatedSorting };
      changed = true;
    }
  });

  return changed ? (result as UserSettings['todayGroupSorting']) : undefined;
};

// Builds the full user-settings patch a task duplication implies for ordering.
// The new `customTaskOrder` store AND the legacy `todoSorting` /
// `todayGroupSorting` stores are updated together: the legacy stores feed
// `reconcileCustomTaskOrder` on read, which lets a diverging legacy entry
// overwrite the new store — so writing only `customTaskOrder` would have the
// freshly inserted ids stripped back out (they then fall to the bottom of the
// list). Keeping both in sync makes reconciliation a no-op.
export const getDuplicatedTaskOrderSettings = (
  settings: DuplicatedTaskOrderSettingsInput | undefined,
  taskPairs: TaskPairs,
): Partial<UserSettings> => {
  if (!settings) {
    return {};
  }

  const result: Partial<UserSettings> = {};

  const customTaskOrder = getDuplicatedCustomTaskOrder(
    settings.customTaskOrder,
    taskPairs,
  );
  if (Object.keys(customTaskOrder).length) {
    result.customTaskOrder = customTaskOrder;
  }

  const todoSorting = getUpdatedOrderRecord(settings.todoSorting, taskPairs);
  if (todoSorting) {
    result.todoSorting = todoSorting;
  }

  const todayGroupSorting = getUpdatedTodayGroupSorting(
    settings.todayGroupSorting,
    taskPairs,
  );
  if (todayGroupSorting) {
    result.todayGroupSorting = todayGroupSorting;
  }

  return result;
};
